/**
 * A dictionary of URL parameters.
 */
type AppUrlParams = { [key: string]: boolean | number | string };

/**
 * The options that can be passed to `ApiSettings.buildQueryString`.
 */
type AppQueryStringOptions = {
    /**
     * Whether to add the `api-key` and `sessionId` parameters to the query string, which will be used instead of the
     * equivalent HTTP headers to match the session cookie. This is only needed for URLs that will be viewed directly
     * instead of fetched through `Client`. Default false.
     */
    auth?: boolean;

    /**
     * The parameters to add to the query string. Default none.
     */
    params?: AppUrlParams;

    /**
     * Whether to add a randomized URL parameter to prevent the browser from returning a cached response. Default
     * false.
     */
    noCache?: boolean;
};

/**
 * The options that can be passed to `ApiSettings.buildAppUrl`.
 */
type AppUrlOptions = AppQueryStringOptions & {
    /**
     * A hash to add to the URL after `#`. If the URL already has a hash, this replaces the previous value. Default
     * none.
     */
    hash?: string;
};

/**
 * The base URL and session context for the backend API.
 */
class ApiSettings {
    /**
     * The base URL for the app, without the protocol but including the path (like `example.medfarsolutions.com/html5`).
     */
    private readonly baseUrl: string;

    /**
     * The auth token for the current session.
     */
    private apiKey: string = "";

    /**
     * The session ID for the current session.
     */
    private sessionId: string = "";

    /**
     * Construct an instance.
     * @param baseUrl The base URL for the app, without the protocol but including the path (like `example.medfarsolutions.com/html5`).
     */
    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;
    }

    /**
     * The base URL for the app, without the protocol but including the path (like `example.medfarsolutions.com/html5`).
     */
    public get BaseUrl(): string {
        return this.baseUrl;
    }

    /**
     * The auth token for the current session.
     */
    public get ApiKey(): string {
        return this.apiKey;
    }

    /**
     * The session ID for the current session.
     */
    public get SessionId(): string {
        return this.sessionId;
    }

    /**
     * Set the session auth fields.
     * @param apiKey The API key (i.e. auth token).
     * @param sessionId The session ID.
     * @param source A human-readable label which indicates why the session is being edited.
     */
    public setSession(apiKey: string, sessionId: string, source: string): void {
        if (apiKey !== this.ApiKey || sessionId !== this.SessionId)
            console.debug(`Updated session for ${source}: `, { from: { apiKey: this.ApiKey, sessionId: this.SessionId }, to: { apiKey, sessionId } });

        this.apiKey = apiKey;
        this.sessionId = sessionId;
    }

    /**
     * Get an absolute URL to the application.
     * @param relativePath The relative path or URL within the application, if any. If this has a query string or hash, they'll be merged into the new URL along with the `options`.
     * @param options The settings which customize the generated app URL.
     */
    public buildAppUrl(relativePath: string = null, options: AppUrlOptions = null): string {
        // get base URL
        const url = new URL(`https://${this.baseUrl}`);

        // merge in relative URL
        if (relativePath) {
            const fromUrl = new URL(relativePath, window.location.origin);

            options ??= {};
            options.params ??= {};

            fromUrl.searchParams.forEach((value, key) => options.params[key] ??= value);

            options.hash ||= fromUrl.hash; // avoid ?? since hash defaults to ''

            url.pathname = `${url.pathname}/${fromUrl.pathname}`.replace(/\/+/g, "/");
        }

        // apply query string & hash options
        return this
            .extendUrl(url, options)
            .toString();
    }

    /**
     * Build a custom query string for a URL, including the `?` prefix.
     * @param options The settings which customize the generated query string.
     */
    public buildQueryString(options: AppQueryStringOptions): string {
        const url = new URL(window.location.origin);
        this.extendUrl(url, options);
        return url.search;
    }

    /**
     * Apply options to a custom URL.
     * @param url The URL to extend.
     * @param options The settings which customize the generated app URL.
     * @returns Returns the input URL for convenience.
     */
    public extendUrl(url: URL, options: AppUrlOptions): URL {
        // add custom parameters
        if (options?.params) {
            for (const key of Object.keys(options.params))
                url.searchParams.append(key, options.params[key]?.toString());
        }

        // add no-cache parameter
        if (options?.noCache)
            url.searchParams.append("_nocache", `${new Date().getTime()}${Math.random().toString(36).substring(2)}`);

        // authenticate
        if (options?.auth) {
            url.searchParams.append("api-key", this.ApiKey);
            url.searchParams.append("sessionId", this.SessionId);
        }

        // add hash
        if (options?.hash)
            url.hash = options.hash;

        return url;
    }

    /**
     * Get an absolute URL to the Myle registered-operations API.
     */
    public buildApiPath(): string {
        return this.buildAppUrl("/api/registeredOperation");
    }
}

export { ApiSettings, AppQueryStringOptions, AppUrlOptions, AppUrlParams };
